import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import ReactGA from 'react-ga4';
import { ChevronRight, ChevronRightIcon } from 'lucide-react';
import { Link } from './Link';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface BreadcrumbNavigationProps {
  items: BreadcrumbItem[];
  className?: string;
  showHome?: boolean;
  trackHomeClick?: boolean;
  trackData?: Record<string, any>;
  useChevronRightIcon?: boolean;
  linkClassName?: string;
  chevronClassName?: string;
}

/**
 * Shared BreadcrumbNavigation component for use across all pages
 * Supports multiple breadcrumb items with optional links and click handlers
 */
const BREADCRUMB_BASE_CLASSES =
  'flex items-center flex-wrap gap-1 text-sm text-gray-600 dark:text-gray-300';

export const BreadcrumbNavigation: React.FC<BreadcrumbNavigationProps> = ({
  items,
  className,
  showHome = true,
  trackHomeClick = false,
  trackData,
  useChevronRightIcon = false,
  linkClassName,
  chevronClassName,
}) => {
  const intl = useIntl();
  const ChevronIcon = useChevronRightIcon ? ChevronRightIcon : ChevronRight;
  const resolvedClassName = [
    BREADCRUMB_BASE_CLASSES,
    className ?? 'mb-4 px-1 sm:px-0',
  ]
    .filter(Boolean)
    .join(' ');

  const handleHomeClick = () => {
    if (trackHomeClick) {
      const isDebugEnabled = typeof window !== 'undefined' && 
        (localStorage.getItem('__DEBUG_RENDER__') === 'true' || 
         new URLSearchParams(window.location.search).has('debug-render'));
      
      if (isDebugEnabled) {
        console.log('[NAV] Breadcrumb home clicked', {
          ...trackData,
          timestamp: new Date().toISOString(),
        });
      }
      
      ReactGA.event({
        category: 'interactions',
        action: 'click_breadcrumb_home',
        label: typeof window !== 'undefined' ? window.location.pathname : undefined,
        nonInteraction: false,
        ...trackData,
      });
    }
  };

  const allItems: BreadcrumbItem[] = showHome
    ? [
        {
          label: intl.formatMessage({
            id: 'navigation.home.breadcrumb',
            defaultMessage: 'Home',
            description: 'Breadcrumb link to home page',
          }),
          href: '/',
          onClick: handleHomeClick,
        },
        ...items,
      ]
    : items;

  return (
    <nav 
      className={resolvedClassName}
      aria-label={intl.formatMessage({ 
        id: 'breadcrumb.navigation', 
        defaultMessage: 'Breadcrumb navigation', 
        description: 'Accessibility label for breadcrumb navigation showing page hierarchy' 
      })}
    >
      {allItems.map((item, index) => {
        const isLast = index === allItems.length - 1;

        const defaultLinkClassName = 'text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-200';
        const defaultChevronClassName = useChevronRightIcon 
          ? 'h-4 w-4 mx-1 sm:mx-2 text-gray-400 dark:text-gray-500' 
          : 'mx-2 text-gray-400 dark:text-gray-500';

        return (
          <React.Fragment key={index}>
            {item.href ? (
              <Link
                href={item.href}
                className={linkClassName || defaultLinkClassName}
                onClick={item.onClick}
              >
                {item.label}
              </Link>
            ) : (
              <span className={isLast ? 'text-gray-800 dark:text-white font-medium' : 'text-gray-600 dark:text-gray-300'}>
                {item.label}
              </span>
            )}
            {!isLast && (
              <ChevronIcon 
                className={chevronClassName || defaultChevronClassName} 
              />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};
